import axios from 'axios'
import type { InternalAxiosRequestConfig } from 'axios'
import {
	ContentApiMercariCampaignAppHomepage,
	ContentApiMercariCampaignSlug,
	ContentApiMercariProductDetail,
	ContentApiMercariProductList,
	type IMercariProductDetailApiResponse,
	type IMercariProductListApiResponse,
	ContentApiMercariLandingBanners,
} from '@/types/Mercari'
import { type IBreadcrumbItem } from '@/types/BreadCrumb'
import {
	shouldUseMercariProdApi,
	isEnableProdQueryFlexibility,
	getMercariUrl,
} from '@/lib/mercari'
import { NotificationTab } from '@/assets/ts/notification'
import { type IContentBrand } from '@/types/Brands'
import {
	productSharingData,
	type IProductSharingSettings,
} from '@/legacy/ts/productSharing'
import { type ICouponResponse } from '@/types/Coupon'
import { type ShoppingDestinationItem } from '@/lib/useFetchShoppingData'
import { type IMercariBrandDetail } from '@/types/MercariBrandDetail'
import { type IBlogThumbnailData } from '@/types/Blog'
import { getStringWithoutSlash } from '@/lib/utils'

const EXPRESS_CHECKOUT_URL = `${window.BUYANDSHIP.settings.productsharing_express_checkout_host}/${window.BUYANDSHIP.settings.current_language}/wp-json`
const INAPP_URL = window.BUYANDSHIP.settings.content_api_url.replace(
	'/api/',
	'/',
)
const locale = import.meta.env.PROD
	? ''
	: window.BUYANDSHIP.settings.current_language
const responseCache: {
	[key: string]: any
} = {}
const promiseCache: {
	[key: string]: Promise<any> | null
} = {}
const defaultPostHeaders = {
	'Content-Type': 'application/json',
	'Accept-Language': 'en',
}

const tokenBuyforyou = localStorage.getItem(
	`tokenBuyforyou_${window.BUYANDSHIP.settings.user_id}`,
)
const instanceBuyforyou = axios.create({
	baseURL: `${window.BUYANDSHIP.settings.buyforyou_api_host}`,
	headers: {
		Authorization: `Bearer ${tokenBuyforyou}`,
	},
})

function getCurrentMercariBaseUrl() {
	const normalUrl = window.BUYANDSHIP.settings.buyforyou_api_host
	// real production enviroment
	if (!isEnableProdQueryFlexibility()) {
		return normalUrl
	}
	// for local or staging enviroment
	if (shouldUseMercariProdApi()) {
		if (import.meta.env.DEV) {
			return import.meta.env.PUBLIC_MERCARI_PROXY_URL_FOR_LOCAL_DEV
		}
		return window.BUYANDSHIP.settings.buyforyou_mercari_preview_prod_api_host
	}
	// fallback
	return normalUrl
}

const instanceMercari = axios.create({
	baseURL: getCurrentMercariBaseUrl(),
	headers: {
		Authorization: `Bearer ${tokenBuyforyou}`,
	},
})

instanceMercari.interceptors.request.use(request => {
	if (request.baseURL !== getCurrentMercariBaseUrl()) {
		instanceMercari.defaults.baseURL = getCurrentMercariBaseUrl()
	}
	return request
})

function requestHandler(
	request: InternalAxiosRequestConfig<any>,
): InternalAxiosRequestConfig<any> {
	if (!request.data) {
		return request
	}
	Object.keys(request.data).forEach(
		key => request.data[key] == null && delete request.data[key],
	)
	return request
}

function getProductSharingHeader() {
	const PStoken =
		localStorage &&
		localStorage.getItem(`tokenProductSharing_${productSharingData.userId}`)
	const tokenParams =
		productSharingData.isLogin && PStoken
			? { Authorization: `Bearer ${PStoken}` }
			: {}
	return {
		...productSharingData.PSHeader,
		...tokenParams,
	}
}
export const instanceProductSharing = axios.create({
	baseURL: productSharingData.apiUrl,
	headers: getProductSharingHeader(),
})

instanceProductSharing.interceptors.request.use(requestHandler)

instanceProductSharing.interceptors.response.use(
	response => response,
	async error => Promise.reject(error),
)

export const instanceBuyandship = axios.create({
	baseURL: window.BUYANDSHIP.settings.api_url,
})

instanceBuyandship.interceptors.request.use(config => {
	if (config.method === 'post') {
		config.headers.set({ ...defaultPostHeaders })
	}
	return config
})

instanceBuyandship.interceptors.response.use(
	response => response.data,
	async err => {
		if (
			err.response &&
			err.response.data &&
			err.response.data.error &&
			err.response.data.error.message
		) {
			const errorWrapper: any = new Error(err.response.data.error.message, {
				cause: err.response,
			})

			if (err.response.data.error.key) {
				errorWrapper.key = err.response.data.error.key
			}

			if (err.response.data.error.code) {
				errorWrapper.code = err.response.data.error.code
			}

			throw errorWrapper
		} else {
			throw err
		}
	},
)

function getCache(functionName: string) {
	if (
		responseCache[functionName] !== undefined &&
		responseCache[functionName] !== null
	) {
		return Promise.resolve(responseCache[functionName])
	}

	if (
		promiseCache[functionName] !== undefined &&
		promiseCache[functionName] !== null
	) {
		return Promise.resolve(promiseCache[functionName])
	}

	return null
}

async function getUserInfo(email = '') {
	return instanceBuyandship
		.get(`/users/search?email=${encodeURIComponent(email)}`)
		.then(res => res.data)
}

async function requestEmailOtp(email = '') {
	return (
		instanceBuyandship
			// .post('/users/request-email-otp?fail=429', {
			.post('/users/request-email-otp', {
				email,
				use_token: 'yes',
			})
			.then(res => res.data)
	)
}

async function verifyEmailOtp(params: { email: string; otp: string }) {
	return (
		instanceBuyandship
			// .post('/users/email-otp?fail=422', {
			// .post('/users/email-otp?fail=410', {
			.post('/users/email-otp', {
				email: params.email,
				otp: params.otp,
			})
			.then(res => res.data)
	)
}

async function updatePassword(params: {
	password: string
	token: string
	email: string
}) {
	return (
		instanceBuyandship
			// .post('/users/update-password?fail=true', {
			.post('/users/update-password', {
				new_password: params.password,
				token: params.token,
				email: params.email,
			})
			.then(res => res.data)
	)
}

async function updateUserFields(params: {
	fullName: string
	inviteCode: string
	newsletter: string
	livingCountry: string
}) {
	return (
		instanceBuyandship
			// .post('/users/me/profile?fail=true', {
			.post('/users/me/profile', {
				full_name: params.fullName,
				invite_code: params.inviteCode,
				newsletter: params.newsletter,
				living_country: params.livingCountry,
			})
			.then(res => res.data)
	)
}

async function deleteAccount(token: string) {
	return instanceBuyandship
		.post('users/delete-account/', { token })
		.then(res => {
			return res
		})
		.catch(error => {
			throw new Error(error)
		})
}

async function connectExpressCheckout() {
	return instanceBuyandship
		.post('connect/expresscheckout', {
			headers: {
				Cookie: document.cookie,
			},
		})
		.then(({ data }) => {
			const { token } = data
			if (token) {
				localStorage.setItem(
					`tokenExpressCheckout_${window.BUYANDSHIP.settings.user_id}`,
					token,
				)
			}
		})
		.catch(error => {
			console.error('Express checkout connection failed:', error)
		})
}

async function postExpressCheckout(data: any) {
	return axios
		.post(`${EXPRESS_CHECKOUT_URL}/buyandshop/checkout`, data, {
			headers: {
				...defaultPostHeaders,
				Authorization: `Bearer ${localStorage.getItem(
					`tokenExpressCheckout_${window.BUYANDSHIP.settings.user_id}`,
				)}`,
			},
		})
		.then(res => res.data)
		.catch(error => {
			const errorObject = new Error()
			Object.assign(errorObject, {
				code: error.response.data.error.code,
				key: error.response.data.error.key,
				message: error.response.data.error.message,
			})
			throw errorObject
		})
}

async function fetchExpressCheckoutViewProduct(slug = '') {
	const timestamp = Math.floor(Date.now() / 1000)
	return axios
		.get(
			`${EXPRESS_CHECKOUT_URL}/buyandshop/view_product/${slug}?_=${timestamp}`,
		)
		.then(res => {
			const { data } = res.data
			return {
				images: data.images,
				limitQuantity: data.limit_quantity,
				postIds: data.post_ids,
				productDetails: {
					name: data.product_details.name,
					price: data.product_details.price,
					localPrice: data.product_details.local_price,
					fulfillmentMode: data.product_details.fulfillment_mode,
					fulfillmentMonth: data.product_details.fulfillment_month,
					fulfillmentRange: data.product_details.fulfillment_range,
				},
				productIds: data.product_ids,
				productType: data.product_type,
				remainStock: data.remain_stock,
				stockStatus: data.stock_status,
				warehouse: data.warehouse,
			}
		})
		.catch(error => {
			throw new Error(error)
		})
}

async function fetchSettingInsurance() {
	const functionName = fetchSettingInsurance.name
	const cache = getCache(functionName)
	if (cache) return cache

	const executePromise = instanceBuyandship
		.get(`settings/${locale}?k[]=insurance`)
		.then(({ data }) => {
			const result: {
				[key: string]: number
			} = {}
			Object.keys(data.insurance.rates).forEach(key => {
				result[key] = data.insurance.rates[key].rate
			})
			responseCache[functionName] = result
			promiseCache[functionName] = null
			return result
		})
		.catch(error => {
			throw new Error(error)
		})

	promiseCache[functionName] = executePromise
	return executePromise
}

function fetchSettingSupportedCurrencies() {
	const functionName = fetchSettingSupportedCurrencies.name
	const cache = getCache(functionName)
	if (cache) return cache

	const executePromise = instanceBuyandship
		.get(`settings/${locale}?k[]=supported_currencies`)
		.then(({ data }) => {
			const result = data.supported_currencies
			responseCache[functionName] = result
			promiseCache[functionName] = null
			return result
		})
		.catch(error => {
			throw new Error(error)
		})

	promiseCache[functionName] = executePromise
	return executePromise
}

function fetchSettingWarehouses() {
	const functionName = fetchSettingWarehouses.name
	const cache = getCache(functionName)
	if (cache) return cache

	const executePromise = instanceBuyandship
		.get(`settings/${locale}?k[]=warehouses`)
		.then(({ data }) => {
			const warehouses = data.warehouses || null
			const map: {
				[key: string]: {
					name: string
					currency: string
					alias: string
				}
			} = {}
			if (warehouses) {
				Object.keys(warehouses).forEach(id => {
					map[id] = {
						name: warehouses[id].name,
						currency: warehouses[id].currency,
						alias: warehouses[id].alias,
					}
				})
			}
			responseCache[functionName] = map
			promiseCache[functionName] = null
			return map
		})
		.catch(error => {
			throw new Error(error)
		})

	promiseCache[functionName] = executePromise
	return executePromise
}

function fetchInappPostReminderHowItWorks() {
	const functionName = fetchInappPostReminderHowItWorks.name
	const cache = getCache(functionName)
	if (cache !== null) {
		return cache
	}

	const executePromise = axios
		.get(`${INAPP_URL}how-it-works/sharing/post/reminder`)
		.then(res => {
			responseCache[functionName] = res
			promiseCache[functionName] = null
			return res
		})
		.catch(error => {
			console.log(error)
		})

	promiseCache[functionName] = executePromise
	return executePromise
}

function fetchInappCheckoutInfo() {
	const functionName = fetchInappCheckoutInfo.name
	const cache = getCache(functionName)
	if (cache !== null) {
		return cache
	}

	const executePromise = axios
		.get(`${INAPP_URL}how-it-works/sharing/post/checkout/info/`)
		.then(res => {
			responseCache[functionName] = res
			promiseCache[functionName] = null
			return res
		})
		.catch(error => {
			console.log(error)
		})

	promiseCache[functionName] = executePromise
	return executePromise
}

function fetchInappCheckoutShippingHowItWorks() {
	const functionName = fetchInappCheckoutShippingHowItWorks.name
	const cache = getCache(functionName)
	if (cache !== null) {
		return cache
	}

	const executePromise = axios
		.get(`${INAPP_URL}how-it-works/sharing/post/checkout/shipping/`)
		.then(res => {
			responseCache[functionName] = res
			promiseCache[functionName] = null
			return res
		})
		.catch(error => {
			console.log(error)
		})

	promiseCache[functionName] = executePromise
	return executePromise
}

async function fetchNotificationList() {
	return instanceBuyandship.get(`notifications`).then(({ data }) => {
		if (data && data.length) {
			return data.map(
				(item: {
					id: string
					type: string
					title: string
					content: string
					created: number
					read: string
					url_web: string
				}) => ({
					id: item.id,
					type: item.type,
					title: item.title,
					content: item.content,
					createdTimestamp: item.created,
					isRead: item.read === 'yes',
					url: item.url_web,
				}),
			)
		}
		return []
	})
}

async function updateNotification(payload: any) {
	const APIurl =
		payload.type === NotificationTab.ALL
			? 'notifications'
			: `notifications/type/${payload.APItype}`
	return instanceBuyandship
		.post(`${APIurl}`, { read: 'yes' })
		.then(data => data)
		.catch(error => {
			throw error
		})
}
async function fetchInappUserBadges() {
	interface IBadge {
		content: string
		title: string
	}

	type BadgeMap = Record<string, IBadge>

	try {
		const response = await axios.get<{ data: { content: BadgeMap } }>(
			`${INAPP_URL}api/sharing/users/badges`,
		)
		return response.data.data.content
	} catch (error) {
		// Improve error message by adding more context
		throw new Error(
			`Failed to fetch user badges: ${
				error instanceof Error ? error.message : error
			}`,
		)
	}
}

async function updateNotificationEach(notificationId: string) {
	return instanceBuyandship
		.post(`notifications/${notificationId}`, { read: 'yes' })
		.then(data => data)
		.catch(error => {
			throw error
		})
}

async function connectBuyforyou() {
	return instanceBuyandship
		.post('connect/buyforyou', {
			headers: {
				Cookie: document.cookie,
			},
		})
		.then(({ data }) => {
			if (data && data.token) {
				localStorage.setItem(
					`tokenBuyforyou_${window.BUYANDSHIP.settings.user_id}`,
					data.token,
				)
				Object.assign(instanceBuyforyou.defaults, {
					headers: { authorization: `Bearer ${data.token}` },
				})
			}
			return data.token
		})
		.catch(error => {
			throw error
		})
}

async function fetchPricingCurrencyBuyforyou() {
	const functionName = fetchPricingCurrencyBuyforyou.name
	const cache = getCache(functionName)
	if (cache) return cache

	const executePromise = instanceBuyforyou
		.get(
			`/bns/pricing/warehouse_currency/${window.BUYANDSHIP.settings.current_country}`,
		)
		.then((res: any) => {
			if (res?.data) {
				const result = res.data.map(
					(item: {
						currency: string
						key: string
						local_currency: string
						is_local: boolean
						rate: number
						currency_decimal: number
					}) => ({
						currency: item.currency,
						key: item.key,
						localCurrency: item.local_currency,
						isLocal: item.is_local,
						rate: item.rate,
						currencyDecimal: item.currency_decimal,
					}),
				)
				responseCache[functionName] = result
				promiseCache[functionName] = null
				return result
			}
			return []
		})
		.catch(error => {
			throw new Error(error)
		})

	promiseCache[functionName] = executePromise
	return executePromise
}

export interface BuyforyouEstimatePayloadItem {
	warehouse: string
	subtotal: string
	site: string
}

export interface BuyforyouEstimatePayload {
	items: BuyforyouEstimatePayloadItem[]
}

export interface BuyforyouEstimateResponseItem {
	total: number
	local_total: number
	warehouse_currency: string
	is_local_currency: boolean
	exchange_rate: number
	local_currency: string
	tax: number
	service_fee: number
	per_transaction_cost: number
}

export interface ISummaryProfile {
	email_verified: string
	onboarded: string
}

// fetch buyforyou estimate pricing list
// async function fetchEstimatePricingListBuyforyou(
//   payload: BuyforyouEstimatePayload,
// ): Promise<BuyforyouEstimateResponseItem[]> {
//   // This api doesn't require auth token, unlike the /pricing/estimate api (This is for PCS)
//   return instanceBuyforyou.post(`/bns/pricing/estimate_list`, payload)
// }

interface IBuyforyouPartners {
	[key: string]: {
		enabled: string
		warehouse: string
		preview_prod_b4u_api_host?: string
	}
}

interface IBuyforyouSettings {
	apiHost: string
	requestWarehouses: {
		[key: string]: string
	}
	partners?: IBuyforyouPartners
	paymentGateways: {
		client_key: string
		environment: string
	}
	formattedBuyforyouWarehouses: string[]
}

export async function fetchSettingBuyforyou(): Promise<IBuyforyouSettings> {
	const functionName = fetchSettingBuyforyou.name
	const cache = await getCache(functionName)

	if (cache) return cache

	try {
		const { data } = await instanceBuyandship.get<{
			buyforyou: {
				api_host: string
				request_warehouses: {
					[key: string]: string
				}
				partners?: IBuyforyouPartners
				payment_gateways: {
					client_key: string
					environment: string
				}
			}
		}>(`settings/${locale}?k[]=buyforyou`)

		const requestWarehouses = data.buyforyou.request_warehouses || {}
		const result = {
			apiHost: data.buyforyou.api_host,
			requestWarehouses,
			partners: data.buyforyou.partners,
			paymentGateways: data.buyforyou.payment_gateways,
			formattedBuyforyouWarehouses: Object.keys(requestWarehouses).reduce(
				(acc: string[], curr: string) => {
					if (requestWarehouses[curr] === 'yes') acc.push(curr)
					return acc
				},
				[] as string[],
			),
		}

		promiseCache[functionName] = null
		responseCache[functionName] = result

		return result
	} catch (error) {
		console.error('Failed to fetch setting: ', error)
		throw error
	}
}

async function fetchBrands() {
	try {
		const response = await axios.get(`${INAPP_URL}api/brands`)
		const result: IContentBrand = response.data.data
		return result
	} catch (error) {
		console.log('Failed to fetch brands: ', error)
		throw error
	}
}

async function fetchProductSharingSettings(): Promise<{
	isAcceptReturnRefund: boolean
	paymentMethod: string[]
	discount: string[]
	bannedRemarks: { [key: string]: string }
	minCreatePostDays: number
	cloudflareImageVariants: { [key: number]: string }
	tabsListStructure: { [key: string]: string[] }
	tabsListDetail: { route: string; slug: string }[]
} | null> {
	try {
		const functionName = fetchProductSharingSettings.name
		const cache = getCache(functionName)
		if (cache) return await cache
		const executePromise = instanceProductSharing
			.get<{ data: IProductSharingSettings }>(
				`/settings?accept_language=${productSharingData.PSParams}`,
			)
			.then(res => {
				if (res.data.data) {
					const {
						accept_return_refund: isAcceptReturnRefund,
						payment_methods: paymentMethod,
						discount,
						banned_remarks: bannedRemarks,
						min_create_post_days: minCreatePostDays,
						cloudflare_image_variants: cloudflareImageVariants,
						tabs: tabsListStructure,
						tabs_detail: tabsListDetail,
					} = res.data.data

					const output = {
						isAcceptReturnRefund,
						paymentMethod,
						discount,
						bannedRemarks,
						minCreatePostDays,
						cloudflareImageVariants,
						tabsListStructure,
						tabsListDetail,
					}
					promiseCache[functionName] = null
					responseCache[functionName] = output
					return output
				}
				return null
			})
		promiseCache[functionName] = executePromise
		return await executePromise
	} catch (error) {
		console.log('Failed to fetch product sharing settings: ', error)
		throw error
	}
}

function fetchSummaryProfile() {
	const functionName = fetchSummaryProfile.name
	const cache = getCache(functionName)
	if (cache) return cache
	const executePromise = instanceBuyandship
		.get<ISummaryProfile>('users/me/summary')
		.then(({ data }) => {
			if (data) {
				const { email_verified: emailVerified, onboarded } = data

				const output = {
					emailVerified,
					onboarded,
				}
				promiseCache[functionName] = null
				responseCache[functionName] = output
				return output
			}
			return null
		})
	promiseCache[functionName] = executePromise
	return executePromise
}

async function fetchCoupons() {
	const {
		user_id: userId,
		current_country: currentCountry,
		current_language: currentLanguage,
		buyforyou_api_host: buyforyouApiHost,
	} = window.BUYANDSHIP.settings

	const headers: Record<string, string> = {
		'Accept-Language': currentLanguage,
	}

	async function getCoupons(token?: string | null) {
		if (token) {
			headers.Authorization = `Bearer ${token}`
		}
		const response = await axios.get<ICouponResponse>(
			`${buyforyouApiHost}/coupons/`,
			{
				params: { current_country: currentCountry },
				headers,
			},
		)
		return response.data
	}

	async function getBuyforyouToken() {
		let token = localStorage.getItem(`tokenBuyforyou_${userId}`)
		if (!token) {
			token = await connectBuyforyou()
		}
		return token
	}

	try {
		// First attempt: try with existing token if available
		if (userId) {
			const token = await getBuyforyouToken()
			return await getCoupons(token)
		}
		return await getCoupons()
	} catch (error) {
		// Second attempt: reconnect and retry if user is logged in
		if (userId) {
			try {
				const newToken = await connectBuyforyou()
				return await getCoupons(newToken)
			} catch (retryError) {
				console.error('Failed to fetch coupons after retry:', retryError)
				throw retryError
			}
		} else {
			console.error('Failed to fetch coupons:', error)
			throw error
		}
	}
}

function getCurrentLanguageAlignWithApp() {
	// Because some language code are different from the app
	const currentLanguage =
		window.BUYANDSHIP.settings.current_language.toLowerCase()
	switch (currentLanguage) {
		case 'ja':
			return 'ja_jp'
		case 'vi':
			return 'vi_vn'
		default:
			return currentLanguage
	}
}

async function fetchMercariCommonInfo() {
	interface IMercariCommonInfo {
		keywords: string[]
		promotion_shops: {
			shop_id: string
			display_name: string
		}[]
		popular_categories: {
			category_id: number
			display_name: string
			image_url: string
		}[]
	}

	try {
		const {
			data: {
				keywords,
				promotion_shops: promotionShops,
				popular_categories: popularCategories,
			},
		} = await instanceMercari.get<IMercariCommonInfo>(
			`/client-assistant/mercari-common-info/?lang=${getCurrentLanguageAlignWithApp()}`,
		)

		return {
			keywords,
			promotionShops,
			popularCategories,
		}
	} catch (error) {
		console.error('Failed to fetch Mercari common info: ', error)
		throw error
	}
}

export interface MercariCategoryItem {
	id: string
	name: string
	level: string
	parent_info: string | null
	has_next_level: boolean
	sub_categories?: MercariCategoryItem[]
}

export interface MercariCategoryListResponse {
	category: MercariCategoryItem[]
	parent?: MercariCategoryParent | null
}

export interface MercariCategoryParent {
	id: string
	name: string
	parent: MercariCategoryParent | null
}

export interface MercariCategoryOption {
	id: string
	label: string
	value: string
}

export function transformCategoriesToOptions(
	categories: MercariCategoryItem[],
): MercariCategoryOption[] {
	return [
		{ id: 'All', label: '全部分類', value: 'All' },
		...categories.map(category => ({
			id: category.id,
			label: category.name,
			value: category.id,
		})),
	]
}

async function fetchMercariCategoryList(): Promise<MercariCategoryListResponse> {
	try {
		const { data } = await instanceMercari.get<MercariCategoryListResponse>(
			`/client-assistant/mercari-category-list/?lang=${getCurrentLanguageAlignWithApp()}&parent_info=true`,
		)

		return data
	} catch (error) {
		console.error('Error fetching Mercari categories:', error)
		return { category: [] }
	}
}

async function fetchMercariProductList(params: any) {
	const {
		keyword = '',
		page = 0,
		limit = 20,
		shopId = '',
		sort = '',
		order = '',
		searchLang = '',
		categoryId = '',
		status = '',
		itemConditionId = '',
		priceMin = '',
		priceMax = '',
		marketplace = '',
	} = params

	const queryParams: Record<string, string> = {
		page: String(page),
		limit: String(limit),
	}

	if (keyword) {
		queryParams.keyword = String(keyword)
	}

	if (shopId) {
		queryParams.shop_id = String(shopId)
	}

	if (sort) {
		queryParams.sort = String(sort)
	}

	if (order) {
		queryParams.order = String(order)
	}

	if (searchLang) {
		queryParams.search_lang = String(searchLang)
	}

	if (categoryId) {
		queryParams.category_id = String(categoryId)
	}

	if (status) {
		queryParams.status = String(status)
	}

	if (itemConditionId) {
		queryParams.item_condition_id = String(itemConditionId)
	}

	if (priceMin) {
		queryParams.price_min = String(priceMin)
	}

	if (priceMax) {
		queryParams.price_max = String(priceMax)
	}

	if (marketplace) {
		queryParams.marketplace = String(marketplace)
	}

	const query = new URLSearchParams(queryParams).toString()
	try {
		const { data } = await instanceMercari.get<IMercariProductListApiResponse>(
			`/client-assistant/mercari-product-list/${query ? '?' : ''}${query}`,
		)

		return new ContentApiMercariProductList(data).toModel()
	} catch (error) {
		console.error('Failed to fetch Mercari product list: ', error)
		throw error
	}
}

async function fetchInappMercariCampaignAppHomepage() {
	try {
		const { data } = await axios.get(`${INAPP_URL}api/app_homepage/`)
		return new ContentApiMercariCampaignAppHomepage(data).toModel()
	} catch (error) {
		console.error('Failed to fetch Mercari campaign app homepage: ', error)
		throw error
	}
}

async function fetchInappMercariCampaignSlug(slug: string) {
	try {
		const { data } = await axios.get(`${INAPP_URL}api/campaign/${slug}`)
		return new ContentApiMercariCampaignSlug(data).toModel()
	} catch (error) {
		console.error('Failed to fetch Mercari campaign slug: ', error)
		throw error
	}
}

async function fetchMercariProductDetail(id: string) {
	const query = new URLSearchParams({
		item_id: id,
		lang: getCurrentLanguageAlignWithApp(),
	}).toString()

	try {
		const { data } =
			await instanceMercari.get<IMercariProductDetailApiResponse>(
				`/client-assistant/mercari-product-detail/?${query}`,
			)

		// const data = {
		// 	product_info: {
		// 		id: 'm87736367347',
		// 		url: 'https://jp.mercari.com/item/m87736367347/',
		// 		name: 'アローラ ナッシーex UR SV7a 楽園ドラゴーナ 092/064',
		// 		description:
		// 			'アローラ ナッシーex UR SV7a 楽園ドラゴーナ 092/064\n\nシリーズ: スカーレット&バイオレット\nセット: SV7a 楽園ドラゴーナ\nカード名: アローラ ナッシーex\n型番: SV7a\nレアリティ: UR\n\n開封後すぐスリーブに入れ保管しております。\n\n目立った傷や汚れはありませんが、初期キズ等細かなキズがあるかもしれません。\n商品画像を確認した上でご理解いただいた方のみご購入ください。\n※神経質な方はご購入はご遠慮下さい。\n\n【注意事項】すり替え防止の為、返品・交換・キャンセルは受け付け致しかねますので予めご了承ください。',
		// 		status: 'on_sale',
		// 		price: 1000,
		// 		currency: 'JPY',
		// 		images: [
		// 			'https://static.mercdn.net/item/detail/orig/photos/m87736367347_1.jpg?1740035254',
		// 			'https://static.mercdn.net/item/detail/orig/photos/m87736367347_2.jpg?1740035254',
		// 		],
		// 		size: null,
		// 		color: null,
		// 		brand: 'ポケモンカードゲーム',
		// 		category: 'ポケモンカードゲーム',
		// 		shipping_payer: '送料込み(出品者負担)',
		// 		item_condition: '新品、未使用',
		// 		max_purchase_quantity: 1,
		// 		original_price: 1100,
		// 		shipping_duration_min_day: 1,
		// 		shipping_duration_max_day: 2,
		// 		discount_total_return_percent: null,
		// 		discount_total_return_absolute: null,
		// 		item_discount_return_absolute: null,
		// 	},
		// 	seller_info: {
		// 		id: '238561327',
		// 		name: 'みちまる。',
		// 		avatar:
		// 			'https://static.mercdn.net/c!/w=300,f=webp,u=0/members/238561327.jpg?1653547930',
		// 		rating_number: 259,
		// 		star_rating_score: 5,
		// 		verified: true,
		// 		info_url: 'https://jp.mercari.com/user/profile/238561327/',
		// 	},
		// 	localization: {
		// 		description:
		// 			'阿羅拉納西ex UR SV7a 樂園龍娜 092/064\n\n系列: 紅與藍\n套裝: SV7a 樂園龍娜\n卡片名稱: 阿羅拉納西ex\n型號: SV7a\n稀有度: UR\n\n開封後立即放入保護套中儲存。\n\n沒有明顯的傷痕或汙垢，但可能會有初期傷痕等細微的刮痕。\n請在確認商品圖片後再進行購買。\n※對商品過於敏感者請勿購買。\n\n【注意事項】為了防止調包，恕不接受退貨、交換或取消，敬請見諒。',
		// 		category: '寶可夢卡牌遊戲',
		// 		item_condition: '全新、未使用',
		// 		shipping_payer: '運費包含（賣家負擔）',
		// 		name: '阿羅拉納西ex UR SV7a 樂園龍娜 092/064',
		// 	},
		// } as IMercariProductDetailApiResponse
		return new ContentApiMercariProductDetail(data).toModel()
	} catch (error) {
		console.error('Failed to fetch Mercari product detail: ', error)
		throw error
	}
}

export async function fetchMercariSubCategories(
	categoryId: string,
): Promise<MercariCategoryListResponse> {
	try {
		const cleanCategoryId = getStringWithoutSlash(categoryId)
		const { data } = await instanceMercari.get<MercariCategoryListResponse>(
			`/client-assistant/mercari-category-list/?lang=${getCurrentLanguageAlignWithApp()}&category_id=${cleanCategoryId}&parent_info=true`,
		)

		return data
	} catch (error) {
		console.error('Failed to fetch Mercari sub-categories:', error)
		throw error
	}
}

async function fetchInappMercariLandingBanners() {
	try {
		const response = await axios.get(`${INAPP_URL}api/mercari-landing-banners`)
		const result = response.data.data.content

		return new ContentApiMercariLandingBanners(result).toModel()
	} catch (error) {
		console.log('Failed to fetch Mercari landing banners: ', error)
		throw error
	}
}

async function buildCategoryBreadcrumbs(
	categoryId: string,
	categoryName?: string,
	mercariHomeText?: string,
): Promise<IBreadcrumbItem[]> {
	const breadcrumbs: IBreadcrumbItem[] = []
	const cleanCategoryId = getStringWithoutSlash(categoryId)

	if (mercariHomeText) {
		const params = new URLSearchParams(window.location.search)
		const source = params.get('source')

		breadcrumbs.push({
			displayName: mercariHomeText,
			categoryId: 'mercari-home',
			link: source
				? getMercariUrl('campaign', { slug: source })
				: getMercariUrl('index'),
		})
	}

	if (!cleanCategoryId || cleanCategoryId === 'all') return breadcrumbs

	try {
		const categoryData = await fetchMercariSubCategories(cleanCategoryId)

		const buildPathFromParent = (
			parent: MercariCategoryParent | null,
		): IBreadcrumbItem[] => {
			const path: IBreadcrumbItem[] = []

			let current = parent
			while (current) {
				path.unshift({
					displayName: current.name,
					categoryId: current.id,
					link: getMercariUrl('search', {
						queryParams: { category_id: current.id },
					}),
				})
				current = current.parent
			}

			return path
		}

		if (categoryData.parent) {
			breadcrumbs.push(...buildPathFromParent(categoryData.parent))
		}

		if (categoryName) {
			breadcrumbs.push({
				displayName: categoryName,
				categoryId: cleanCategoryId,
				link: getMercariUrl('search', {
					queryParams: { category_id: cleanCategoryId },
				}),
			})
		}

		return breadcrumbs
	} catch (error) {
		console.error('Failed to build category breadcrumbs:', error)
		return breadcrumbs
	}
}

async function fetchGeoRedirectRecommendation() {
	return instanceBuyandship
		.get(`/recommended/geo`)
		.then(({ data }) => data)
		.catch(error => {
			throw error
		})
}

async function fetchShoppingDestinationDataApi(): Promise<
	ShoppingDestinationItem[]
> {
	try {
		const { data } = await axios.get(
			`${window.BUYANDSHIP.settings.content_api_url}shopping-destinations/`,
		)
		return data.data.content.shoppingDestinations
	} catch (error) {
		console.error('Failed to fetch shopping destination data:', error)
		throw error
	}
}

async function fetchMercariBrandDetail(id: string) {
	if (!id) throw new Error('Mercari brand id is required')
	try {
		const { data } = await axios.get<IMercariBrandDetail>(
			`${window.BUYANDSHIP.settings.content_api_url}brand-categories/${id}`,
		)
		return data
	} catch (error) {
		console.error('Failed to fetch Mercari brand:', error)
		throw error
	}
}

async function fetchMercariBrandBlogList(id: string, fallbackCategory: string) {
	if (!id || !fallbackCategory)
		throw new Error('Mercari brand id and fallback category are required')
	try {
		const { data } = await instanceBuyandship.get(
			`/blog/search?q=${id}&fallback_category=${fallbackCategory}`,
		)
		return data.posts as IBlogThumbnailData[]
	} catch (error) {
		console.error('Failed to fetch Mercari brand blog list:', error)
		throw error
	}
}

async function fetchInappYahooLandingBanners() {
	try {
		const response = await axios.get(`${INAPP_URL}api/yahoo-landing-banners`)
		const result = response.data.data.content

		return new ContentApiMercariLandingBanners(result).toModel()
	} catch (error) {
		console.log('Failed to fetch Mercari landing banners: ', error)
		throw error
	}
}

export {
	connectBuyforyou,
	connectExpressCheckout,
	deleteAccount,
	fetchBrands,
	fetchExpressCheckoutViewProduct,
	fetchNotificationList,
	fetchSettingInsurance,
	fetchSettingSupportedCurrencies,
	fetchSettingWarehouses,
	fetchInappPostReminderHowItWorks,
	fetchInappCheckoutInfo,
	fetchInappCheckoutShippingHowItWorks,
	fetchMercariCommonInfo,
	fetchMercariProductDetail,
	fetchMercariProductList,
	fetchMercariCategoryList,
	fetchInappMercariCampaignAppHomepage,
	fetchInappMercariCampaignSlug,
	fetchPricingCurrencyBuyforyou,
	// fetchEstimatePricingListBuyforyou,
	fetchInappUserBadges,
	fetchProductSharingSettings,
	fetchSummaryProfile,
	getUserInfo,
	postExpressCheckout,
	requestEmailOtp,
	updateNotification,
	updateNotificationEach,
	updatePassword,
	updateUserFields,
	verifyEmailOtp,
	fetchCoupons,
	fetchInappMercariLandingBanners,
	buildCategoryBreadcrumbs,
	fetchGeoRedirectRecommendation,
	fetchShoppingDestinationDataApi,
	fetchMercariBrandDetail,
	fetchMercariBrandBlogList,
	fetchInappYahooLandingBanners,
}
